import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeStatusBadge } from '@/components/ui/tee-status-badge';
import { useAssistant } from '@/contexts/AssistantContext';

/**
 * Entity / module / none / pinned context row under the desktop panel header
 * (README C1-C8, X1).
 */
export function AssistantContextRow() {
  const { t } = useTranslation();
  const { pinned, effectiveContext, pin, unpin } = useAssistant();

  const rowClass =
    'flex items-center justify-between gap-3 border-b border-[var(--border-default)] px-[18px] py-[10px] text-[12px] text-[var(--text-content-sub-title)]';

  const target = pinned ?? effectiveContext;

  if (!target) {
    return (
      <div className={rowClass}>
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden="true"
            data-testid="context-dot-none"
            className="h-[7px] w-[7px] shrink-0 rounded-full border-[1.5px] border-solid border-[var(--text-content-sub-title)] bg-transparent"
          />
          <span className="truncate">{t('assistant.context.noneHint')}</span>
        </span>
      </div>
    );
  }

  const isPinned = !!pinned;
  const isModule = target.type === 'module';
  const status = !isModule && 'status' in target ? target.status : undefined;
  const prefix = isPinned
    ? t('assistant.context.pinned')
    : target.type === 'module'
      ? target.module
      : t(`assistant.context.types.${target.type}`);

  return (
    <div className={rowClass}>
      <span className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className={
            isPinned
              ? 'h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--color-gold-500)]'
              : 'h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--color-forest)]'
          }
        />
        <span className="truncate">
          {prefix}
          {' · '}
          <strong className="font-semibold text-[var(--text-content-title)]">
            {target.label}
          </strong>
        </span>
        {status ? <TeeStatusBadge status={status} size="s" /> : null}
      </span>
      <TeeButton
        variant="tertiary"
        size="s"
        onClick={isPinned ? unpin : pin}
        text={isPinned ? t('assistant.context.unpin') : t('assistant.context.pin')}
      />
    </div>
  );
}
